/**
 * WaitlistCard — the waitlist as /register and the auth modal offer it while
 * sign-ups are closed: the form, what happens after joining (a real sequence:
 * confirm the address, then the invitation), and once sent, where the link
 * went with a way back to use another address.
 */
import React, { useState } from 'react'

import { WaitlistForm, type WaitlistPrefill } from '@platform/auth/components/WaitlistForm'
import { useTranslation } from '@platform/i18n/hooks/useTranslation'

interface WaitlistCardProps {
  source: string
  prefill?: WaitlistPrefill
}

export const WaitlistCard: React.FC<WaitlistCardProps> = ({ source, prefill }) => {
  const { t } = useTranslation('auth')
  const [joined, setJoined] = useState<string | null>(null)
  // a new form after «use another address»: its prefill (the landing hand-off) is spent
  const [round, setRound] = useState(0)

  // the crypto-* tokens follow the theme themselves (CSS variables): no light/dark branch here
  const titleClasses = 'text-crypto-text'
  const mutedClasses = 'text-crypto-muted'
  const ruleClasses = 'border-crypto-border'

  if (joined !== null) {
    return (
      <div className="text-center" data-testid="waitlist-success" role="status">
        <div className="w-14 h-14 rounded-full bg-crypto-accent/10 text-crypto-accent flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className={`text-xl font-semibold mb-2 ${titleClasses}`}>{t('waitlist.successTitle')}</h3>
        <p className={`mb-5 ${mutedClasses}`} data-testid="waitlist-sent-to">
          {t('waitlist.sentTo', { email: joined })}
        </p>
        <button
          type="button"
          onClick={() => {
            setJoined(null)
            setRound((r) => r + 1)
          }}
          className="text-sm text-crypto-accent hover:text-crypto-accent-light font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-crypto-accent rounded"
          data-testid="waitlist-use-another"
        >
          {t('waitlist.useAnother')}
        </button>
      </div>
    )
  }

  return (
    <div>
      <WaitlistForm
        key={round}
        source={source}
        {...(round === 0 && prefill ? { prefill } : {})}
        onJoined={(email) => setJoined(email)}
      />
      <div className={`mt-6 pt-5 border-t ${ruleClasses}`} data-testid="waitlist-next">
        <h3 className={`text-sm font-medium mb-3 ${titleClasses}`}>{t('waitlist.nextTitle')}</h3>
        <ol className="space-y-3">
          {[t('waitlist.stepConfirm'), t('waitlist.stepInvite')].map((step, i) => (
            <li key={step} className="flex items-start gap-3">
              <span className="flex-none w-6 h-6 rounded-full bg-crypto-accent/10 text-crypto-accent text-xs font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              <span className={`text-sm leading-6 ${mutedClasses}`}>{step}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}
